import { gql } from '@apollo/client';
import * as MENUS from '../library/constants/menus';
import { SiteInfoFragment } from '../fragments/GeneralSettings';
import { EditorBlocks } from '../fragments/EditorBlocks';
import { Header, HeaderNavigationMenu, FooterNavigationMenu, SEO, DisclosureBlock, FeaturedImage, PostHero, PostBody, GlobalElements } from '@/components';
import { flatListToHierarchical } from '@faustwp/core';
import { usePageLoadActions } from '@/hooks/usePageLoadActions';

export default function Component(props) {
	// Loading state for previews
	if (props.loading) {
		return <>Loading...</>;
	}

	const { title: siteTitle, description: siteDescription } = props?.data?.generalSettings;
	const { editorBlocks } = props.data.post;
	const blockList = flatListToHierarchical(editorBlocks, { childrenKey: 'innerBlocks' });
	const headerMenuItems = props?.data?.headerMenuItems?.nodes ?? [];
	const footerMenuItems = props?.data?.footerMenuItems?.nodes ?? [];
	const { featuredImage, seo, disclosures, navigationSettings, author, date, title: postTitle, tags, categories, relatedPosts } = props?.data?.post;

	usePageLoadActions();

	return (
		<GlobalElements siteTitle={siteTitle} siteDescription={siteDescription} seo={seo} featuredImageUrl={featuredImage?.node?.sourceUrl} headerMenuItems={headerMenuItems} navigationSettings={navigationSettings} footerMenuItems={footerMenuItems} disclosures={disclosures} pageTitle={postTitle}>
			<PostHero title={postTitle} image={featuredImage} categories={categories} />
			<PostBody date={date} author={author} blocks={blockList} tags={tags.nodes} relatedPosts={relatedPosts.posts?.nodes} categories={categories.nodes} />
		</GlobalElements>
	);
}

Component.variables = ({ databaseId }, ctx) => {
	return {
		databaseId,
    postFilter: databaseId,
		headerLocation: MENUS.HEADER_NAV,
		footerLocation: MENUS.FOOTER_NAV,
		asPreview: ctx?.asPreview,
	};
};

Component.query = gql`
	${SiteInfoFragment}
	${EditorBlocks}
	${Header.fragments.entry}
	${HeaderNavigationMenu.fragments.entry}
	${FooterNavigationMenu.fragments.entry}
	${DisclosureBlock.fragments.entry}
	${SEO.fragments.entry}
	${FeaturedImage.fragments.entry}
	query GetPageData($databaseId: ID!, $postFilter: [ID], $headerLocation: MenuLocationEnum, $headerMobileCtaLocation: MenuLocationEnum, $footerLocation: MenuLocationEnum, $asPreview: Boolean = false) {
		post(id: $databaseId, idType: DATABASE_ID, asPreview: $asPreview) {
			seo {
				...PageSeo
			}
			disclosures {
				...PageDisclosuresFragment
			}
			navigationSettings {
				...PageNavigationSettings
			}
			title
			date
			author {
				node {
					name
					avatar(size: 120) {
						url
					}
				}
			}
			categories {
				nodes {
					uri
					name
          posts(first: 3, where: {notIn: $postFilter}) {
            nodes {
              ...FeaturedImageFragment
              uri
              title
              excerpt(format: RENDERED)
              id
            }
          }
				}
			}
      relatedPosts {
        posts(first: 3) {
          nodes {
            ... on Post {
              ...FeaturedImageFragment
              uri
              title
              excerpt(format: RENDERED)
              id
            }
          }
        }
      }
			tags {
				nodes {
					name
					uri
				}
			}
			...FeaturedImageFragment
			editorBlocks {
				...AllBlocks
			}
		}
		generalSettings {
			...SiteInfoFragment
		}
		footerMenuItems: menuItems(first: 100, where: { location: $footerLocation }) {
			nodes {
				...FooterNavigationMenuItemFragment
			}
		}
		headerMenuItems: menuItems(first: 100, where: { location: $headerLocation }) {
			nodes {
				...HeaderNavigationMenuItemFragment
			}
		}
	}
`;
